import { qualifySelector } from "../utils";
import {
  GET_CART_REQUEST,
  GET_CART_SUCCESS,
  GET_CART_FAILURE,
  SET_VOUCHER_CODE,
  POST_VOUCHER_CODE_FAILURE,
  SET_INPUT_DIRTY,
  SET_COUNTRY_CODE,
} from "../constants";

import orderLine from "../models/orderLine";
import mapAddonProductsToDomains from "../utils/mapAddonProductsToDomains";
import { getTldsFromSession, getTldsFromCart } from "../../lib/getListOfTlds";

const initialState = {
  cartLoaded: false,
  cartErrored: false,
  cartLoading: false,
  price: {},
  cartItems: [],
  tlds: getTldsFromSession(),
  countryCodeFromForm: window.sessionStorage.getItem("customerCountryCode"),
  bundleDiscountActive: false,
  bundleDiscountPercentage: false,
  chosenPackage: "",
  voucherCode: "",
  voucherCodeErrored: false,
  voucherCodeErrorResponseCode: 0,
  addonProducts: [],
  hostingAutoAddedToCart: null,
  isBeginnerRemovedInTheLastUpdate: false,
  isVoucherInputDirty: false,
  difmConsultRequested: false,
  basketDiscountSkippedDueToOrderCount: false,
};

function reducer(state = initialState, action) {
  const { payload } = action;

  switch (action.type) {
    case GET_CART_REQUEST:
      return {
        ...state,
        cartLoaded: false,
        cartErrored: false,
        cartLoading: true,
      };

    case GET_CART_SUCCESS:
      const addonProducts = mapAddonProductsToDomains(payload.productPrices);
      const cartItems = payload.productPrices
        ? payload.productPrices.map((product) => {
            return orderLine(product, payload.periodInMonths);
          })
        : [];

      return {
        ...state,
        cartLoaded: true,
        cartErrored: false,
        cartLoading: false,
        price: {
          ...state.price,
          total: payload.total,
          totalVat: payload.totalVat,
          vatPercent: payload.vatPercent,
          currency: payload.currency,
          defaultIncludeVat: payload.defaultIncludeVat,
          displayCurrency: payload.currency,
          subTotal: payload.subTotal,
          totalRebate: payload.totalRebate,
          approxTotal: payload.approxTotal,
          niceCurrency: payload.niceCurrency,
          approxCurrency: payload.approxCurrency,
        },
        cartItems: cartItems,
        tlds: getTldsFromCart(cartItems),
        bundleDiscountActive: payload.bundleDiscountActive,
        bundleDiscountPercentage: payload.bundleDiscountPercentage,
        chosenPackage:
          payload.productPrices && payload.productPrices.length > 0
            ? cartItems[0].webSpaceSize
            : "",
        addonProducts: addonProducts,
        voucherCode: payload.couponCode,
        couponPctDiscount: payload.couponPctDiscount,
        storeFrontPriceInterval: payload.storeFrontPriceInterval,
        periodInMonths: payload.periodInMonths,
        hostingAutoAddedToCart: window.sessionStorage.getItem(
          "hostingAutoAddedToCart",
        ),
        // Package was Beginner in the previous step and changed now
        isBeginnerRemovedInTheLastUpdate:
          state.chosenPackage === "PPS_SMALL" &&
          (cartItems.length === 0 || cartItems[0].webSpaceSize !== "PPS_SMALL"),
        difmConsultRequested: payload.difmConsultRequested,
        basketDiscountSkippedDueToOrderCount:
          payload.basketDiscountSkippedDueToOrderCount,
      };

    case GET_CART_FAILURE:
      return {
        ...state,
        cartLoaded: false,
        cartErrored: true,
        cartLoading: false,
        cartItems: [],
        tlds: [],
        bundleDiscountActive: false,
        bundleDiscountPercentage: false,
      };

    case SET_VOUCHER_CODE:
      return {
        ...state,
        voucherCode: payload === "delete" ? "" : payload,
        voucherCodeErrored: false,
        isVoucherInputDirty: false,
      };

    case POST_VOUCHER_CODE_FAILURE:
      const { status } = action.error.details;
      return {
        ...state,
        voucherCode: "",
        voucherCodeErrored: true,
        voucherCodeErrorResponseCode: status || 400,
      };

    case SET_INPUT_DIRTY:
      return {
        ...state,
        isVoucherInputDirty: true,
        voucherCodeErrored: false,
      };

    case SET_COUNTRY_CODE:
      return {
        ...state,
        countryCodeFromForm: action.payload,
      };

    default:
      return {
        ...state,
      };
  }
}

export const name = "cart";
export default { [name]: reducer };

export const isCartLoaded = qualifySelector(name, (state) => state.cartLoaded);
export const isCartLoading = qualifySelector(
  name,
  (state) => state.cartLoading,
);
export const isCartErrored = qualifySelector(
  name,
  (state) => state.cartErrored,
);
export const getCartItems = qualifySelector(name, (state) => state.cartItems);
export const getCartPrice = qualifySelector(name, (state) => state.price);
export const hasVPSInCart = qualifySelector(name, (state) => {
  const cartProductPricesFromSession =
    window.sessionStorage.getItem("cart") &&
    JSON.parse(window.sessionStorage.getItem("cart")).productPrices;

  const cartItemsSession =
    cartProductPricesFromSession &&
    cartProductPricesFromSession.length > 0 &&
    !!cartProductPricesFromSession[0].lines.find(
      ({ category }) =>
        category === "onevps-base-plan" ||
        category === "managed-onevps-base-plan",
    );

  const cartItems =
    state.cartItems.length > 0 &&
    !!state.cartItems[0].lines.find(
      ({ category }) =>
        category === "onevps-base-plan" ||
        category === "managed-onevps-base-plan",
    );

  return cartItems || cartItemsSession || false;
});
export const getBundleDiscountActive = qualifySelector(
  name,
  (state) => state.bundleDiscountActive,
);
export const getBundleDiscountPercentage = qualifySelector(
  name,
  (state) => state.bundleDiscountPercentage,
);
export const getChosenPackage = qualifySelector(
  name,
  (state) => state.chosenPackage,
);
export const getPackagesInCartCount = qualifySelector(
  name,
  (state) =>
    state.cartItems.filter(
      ({ webSpaceSize }) => webSpaceSize && webSpaceSize !== "DOMAIN_ONLY",
    ).length,
);
export const getVoucherCode = qualifySelector(
  name,
  (state) => state.voucherCode,
);

export const getVoucherDiscount = qualifySelector(
  name,
  (state) => state.couponPctDiscount,
);

export const getVoucherCodeErrored = qualifySelector(
  name,
  (state) => state.voucherCodeErrored,
);

export const getVoucherCodeErrorResponseCode = qualifySelector(
  name,
  (state) => state.voucherCodeErrorResponseCode,
);
export const getHostingAutoAddedToCart = qualifySelector(
  name,
  (state) => state.hostingAutoAddedToCart,
);

export const getAddonProductsInCart = qualifySelector(
  name,
  (state) => state.addonProducts,
);

export const getIsBeginnerRemovedInTheLastUpdate = qualifySelector(
  name,
  (state) => state.isBeginnerRemovedInTheLastUpdate,
);

export const isDomainPresent = qualifySelector(name, (state) => {
  let cartFromSession = JSON.parse(window.sessionStorage.getItem("cart"));
  cartFromSession = cartFromSession && cartFromSession.productPrices;

  const cartItems =
    state.cartItems.length > 0 ? state.cartItems : cartFromSession || [];

  const isDomain = cartItems.some(
    (item) => item.fullDomain !== "nodomain.notld",
  );

  return isDomain;
});

export const getStoreFrontPriceInterval = qualifySelector(
  name,
  (state) => state.storeFrontPriceInterval,
);

export const getTLDsFromCart = qualifySelector(name, (state) => state.tlds);

export const getPeriodInMonths = qualifySelector(
  name,
  (state) => state.periodInMonths,
);

export const getCustomerCountryFromSession = qualifySelector(name, (state) => {
  // First try to see if we have already saved a customerCountryCode in the session
  let customerCountryCode = state.countryCodeFromForm;

  // If we reach here and countryCode is still null, use the IP Country
  if (!customerCountryCode) {
    // The IP country of the customer, used for fallback
    const { countryCode } = window.onecomConfig;
    customerCountryCode = countryCode;
  }

  return customerCountryCode;
});

export const hasDIFMConsultInCart = qualifySelector(
  name,
  (state) => state.difmConsultRequested,
);

export const getCartDiscountSkipped = qualifySelector(
  name,
  (state) => state.basketDiscountSkippedDueToOrderCount,
);

export const getDomainsInCartWithDomainProtection = qualifySelector(
  name,
  (state) => {
    const domainProtectionAddonsInCart = state.addonProducts.filter(
      (addon) => addon.name === "DNS_ADMIN",
    );

    const domainsInCartWithDomainProtection = [];

    domainProtectionAddonsInCart.forEach(({ domains }) =>
      domainsInCartWithDomainProtection.push(...domains),
    );

    return domainsInCartWithDomainProtection;
  },
);
